import { forwardRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Slide from "@mui/material/Slide";
import { useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { obtenerDescripcionPago } from "./helpers/mediosPago";

// ─── Slide up transition (mobile) ────────────────────────────────────────────
const SlideUp = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const capitalize = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1).replace(/([A-Z])/g, " $1");

const mapearTituloEnvio = (tipo) => {
  if (!tipo) return "Tipo de envío";
  const map = {
    sucursal:   "Entrega en Sucursal",
    transporte: "Envío por Transporte",
    local:      "Retiro en el Local",
    andreani:   "Envío por Andreani",
  };
  return map[tipo] || capitalize(tipo);
};

// ─── Par clave/valor ──────────────────────────────────────────────────────────
const KVItem = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ bgcolor: "#f8fafc", borderRadius: "10px", px: 1.75, py: 1.25, border: "1.5px solid #f1f5f9" }}>
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1e293b", wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  </Grid>
);

// ─── Renderizador recursivo ───────────────────────────────────────────────────
const renderKeyValue = (obj, parentKey = "", datosPago = null) => {
  if (!obj || typeof obj !== "object") return null;

  return Object.entries(obj).flatMap(([key, value]) => {
    if (key === "Tarjeta") return [];

    const displayKey = parentKey
      ? `${parentKey}: ${capitalize(key)}`
      : capitalize(key);

    if (key === "medioPago") {
      const descripcion = obtenerDescripcionPago(value, datosPago?.Tarjeta);
      return [<KVItem key={displayKey} label="Medio de Pago" value={descripcion} />];
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      return renderKeyValue(value, displayKey, datosPago);
    }

    return [
      <KVItem
        key={displayKey + value}
        label={displayKey}
        value={String(value ?? "No disponible")}
      />,
    ];
  });
};

// ─── Section header interno ───────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon sx={{ fontSize: 17, color: "#6366f1" }} />
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1e293b" }}>
      {title}
    </Typography>
  </Box>
);

// ─── Modal principal ──────────────────────────────────────────────────────────
export const ObservacionModal = ({ open, onClose, observacion, pedidoNro }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  let datosCli;
  try {
    datosCli = typeof observacion === "string" ? JSON.parse(observacion) : observacion;
  } catch {
    datosCli = null;
  }

  const isInvalid = !datosCli || typeof datosCli !== "object";
  const { pago = null, obs_envio = null } = isInvalid ? {} : datosCli;
  const tituloEnvio = mapearTituloEnvio(obs_envio?.tipo);

  const paperSx = {
    borderRadius: isMobile ? "20px 20px 0 0" : "20px",
    overflow: "hidden",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.2)",
    ...(isMobile && { position: "fixed", bottom: 0, left: 0, right: 0, m: 0, maxWidth: "100% !important", width: "100%" }),
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={isMobile ? SlideUp : undefined}
      PaperProps={{ sx: paperSx }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 19, color: "#6366f1" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", lineHeight: 1.2 }}>
              {pedidoNro ? `Pedido N° ${pedidoNro}` : "Datos del Pedido"}
            </Typography>
            {!isInvalid && obs_envio?.tipo && (
              <Chip
                label={tituloEnvio}
                size="small"
                sx={{ mt: 0.25, height: 18, fontSize: "0.68rem", fontWeight: 600, bgcolor: "#eef2ff", color: "#6366f1", border: "1px solid #c7d2fe" }}
              />
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ bgcolor: "#f1f5f9", borderRadius: "8px", width: 34, height: 34 }}
        >
          <CloseIcon sx={{ fontSize: 17, color: "#64748b" }} />
        </IconButton>
      </Box>

      {/* ── Contenido ── */}
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#fff" }}>

        {/* Sin datos */}
        {isInvalid ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography sx={{ fontSize: "0.875rem", color: "#94a3b8" }}>
              No hay datos para mostrar.
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Datos de envío */}
            <SectionHeader icon={LocalShippingOutlinedIcon} title="Datos de Envío" />
            <Grid container spacing={1.5}>
              {renderKeyValue(obs_envio)}
            </Grid>

            {/* Datos de pago */}
            {pago !== null && (
              <>
                <Divider sx={{ my: 2.5, borderColor: "#f1f5f9" }} />
                <SectionHeader icon={PaymentOutlinedIcon} title="Datos de Pago" />
                <Grid container spacing={1.5}>
                  {renderKeyValue(pago, "", pago)}
                </Grid>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};